import { useEffect, useRef } from 'react';
import { useAutosaveActive } from '@/lib/autosave/autosaveAuth';
import { useTreeStore } from '@/stores/treeStore';
import { useBackend } from '@/hooks/useBackend';
import { extractNodeType } from '@/lib/tree/treeUtils';
import { nodeIsReadOnlyForUi } from '@/lib/tree/treeNodeTypeConfigs';
import type { NodeType } from '@/types/tree/base';
import { saveEntity } from '@/lib/entity/entitySaveUtils';
import { log } from '@/lib/utils/logger';
import { useSnackbar } from '@/hooks/useSnackbar';
import { useDialogStore } from '@/stores/dialogStore';
import { SampleActivitySaveIncompleteError } from '@/lib/tree/submitEntityUtils';

/**
 * Hook that watches for modified entities and automatically saves them after a debounce period
 * Only saves when autosave is enabled
 */
export function useAutosave() {
  const enabled = useAutosaveActive();
  const modifiedData = useTreeStore((state) => state.modifiedData);
  const backend = useBackend();
  const { showSnackbar } = useSnackbar();
  const { showConfirmDialog } = useDialogStore();

  // Map of dataId -> timeout ID for debouncing
  const debounceTimers = useRef<Map<string, NodeJS.Timeout>>(new Map());

  useEffect(() => {
    if (!enabled) {
      // Clear all pending timers if autosave is disabled
      debounceTimers.current.forEach((timer) => clearTimeout(timer));
      debounceTimers.current.clear();
      return;
    }

    // Clear timers only for entities that are no longer modified
    // This ensures pending saves for still-modified entities continue
    debounceTimers.current.forEach((timer, dataId) => {
      if (!modifiedData.has(dataId)) {
        clearTimeout(timer);
        debounceTimers.current.delete(dataId);
      }
    });

    // Process each modified dataId
    modifiedData.forEach((dataId) => {
      // Skip if there's already a pending timer for this entity
      if (debounceTimers.current.has(dataId)) {
        return;
      }

      // Set up new debounced save
      const timer = setTimeout(async () => {
        try {
          const treeStore = useTreeStore.getState();

          // Get the data - check if still modified (might have been saved manually)
          if (!treeStore.modifiedData.has(dataId)) {
            log.debug('[Autosave] Entity no longer modified, skipping save:', dataId);
            debounceTimers.current.delete(dataId);
            return;
          }

          // Check if this is a sample requests dataId (format: "sampleRequests-project-{projectId}")
          if (dataId.startsWith('sampleRequests-project-')) {
            const projectId = parseInt(dataId.replace('sampleRequests-project-', ''), 10);
            if (isNaN(projectId)) {
              log.warn('[Autosave] Invalid project ID in sample requests dataId:', dataId);
              debounceTimers.current.delete(dataId);
              return;
            }

            const requests = treeStore.getSampleRequests(projectId);
            if (!requests) {
              log.warn('[Autosave] No sample requests found for projectId:', projectId);
              debounceTimers.current.delete(dataId);
              return;
            }

            log.debug('[Autosave] Saving sample requests:', { projectId, count: requests.length });

            // Check if there are any valid requests before attempting to save
            // Only start autosave if at least one request has a title
            // Note: requests in store are clean DTOs, so we check title directly
            const hasValidRequest = requests.some((req) => {
              // For requests without an id, they're new and need a title
              return req.id ? true : req.title?.trim();
            });

            if (!hasValidRequest) {
              log.debug('[Autosave] No requests with titles yet, skipping autosave');
              treeStore.setDataModified(dataId, false);
              debounceTimers.current.delete(dataId);
              return;
            }

            // Filter out empty new requests (those without id need a title)
            const requestsToSave = requests.filter((req) => {
              // New requests (no id) must have at least a title
              return req.id ? true : req.title?.trim();
            });

            if (requestsToSave.length === 0) {
              log.debug(
                '[Autosave] No sample requests to save (no valid requests with titles), clearing modified flag'
              );
              treeStore.setDataModified(dataId, false);
              debounceTimers.current.delete(dataId);
              return;
            }

            // Requests in store are already clean DTOs, no conversion needed
            const dtos = requestsToSave;

            await backend.project.updateSampleRequests(projectId, dtos);

            // Reload from server to get fresh data (API expects string idOrLabel)
            const updatedRequests = await backend.project.getSampleRequests(projectId.toString());
            treeStore.setSampleRequests(projectId, updatedRequests);

            // Clear the modified flag
            treeStore.setDataModified(dataId, false);

            log.debug('[Autosave] Successfully saved sample requests:', { projectId });
            return;
          }

          // Find a node with this dataId to get the type
          const node = Array.from(treeStore.nodes.values()).find((n) => n.dataId === dataId);
          if (!node) {
            log.warn('[Autosave] No node found for dataId:', dataId);
            debounceTimers.current.delete(dataId);
            return;
          }

          const data = treeStore.getData(dataId);
          if (!data) {
            log.warn('[Autosave] No data found for dataId:', dataId);
            debounceTimers.current.delete(dataId);
            return;
          }

          // Prefer node.type (camelCase NodeType); dataId prefix is lowercased and not a NodeType.
          const type = (node.type as NodeType) || extractNodeType(dataId);

          // Skip read-only types that shouldn't be autosaved
          if (nodeIsReadOnlyForUi(type)) {
            log.debug('[Autosave] Skipping autosave for read-only type:', type);
            debounceTimers.current.delete(dataId);
            return;
          }

          log.debug('[Autosave] Saving entity:', { dataId, type });

          const sampleActivityPreconditionDeps =
            type === 'sampleActivity' ? { showSnackbar, showConfirmDialog } : undefined;

          // Save the entity using the unified save utility
          const updatedData = await saveEntity(type, dataId, data, backend, sampleActivityPreconditionDeps);

          // Apply server response without marking dirty (updateData would flip modified on then off)
          treeStore.replaceEntityFromServer(node.nodeId, updatedData);

          log.debug('[Autosave] Successfully saved entity:', { dataId, type });
        } catch (error) {
          if (error instanceof SampleActivitySaveIncompleteError) {
            log.debug('[Autosave] Sample activity save incomplete (e.g. precondition declined); keeping modified flag');
          } else {
            log.error('[Autosave] Error saving entity:', { dataId, error });
          }
          // Don't clear the modified flag on error - let user retry manually
        } finally {
          debounceTimers.current.delete(dataId);
        }
      }, 20000); // 20 second debounce

      debounceTimers.current.set(dataId, timer);
    });

    // Cleanup: don't clear timers when dependencies change
    // Pending saves should complete even if the component re-renders
    // Timers are only cleared when autosave is disabled (handled above)
    // or when entities are no longer modified (handled above)
    return () => {
      // No cleanup needed - let pending saves complete
    };
  }, [enabled, modifiedData, backend, showSnackbar, showConfirmDialog]);
}
